import PermissionSelect from '@adsk/alloy-react-permission-select';
import { SERVICE_ACCESSES } from '@common/constants';
import {
  PROGRESS_TRACKING_PERMISSION_ACTIONS,
  PROGRESS_TRACKING_PERMISSION_LEVELS,
} from '../../constants';
import { getPillValue } from './utils';

const actionsToLevel = (actions) => {
  if (
    actions.some(
      (action) => action === PROGRESS_TRACKING_PERMISSION_ACTIONS.CONFIGURE
    )
  ) {
    return PROGRESS_TRACKING_PERMISSION_LEVELS.CREATE;
  }
  if (
    actions.some(
      (action) => action === PROGRESS_TRACKING_PERMISSION_ACTIONS.TRACK
    )
  ) {
    return PROGRESS_TRACKING_PERMISSION_LEVELS.UPDATE;
  }
  return PROGRESS_TRACKING_PERMISSION_LEVELS.VIEW;
};

const getRolePermissions = (rolesPermissions, rolesInfo) => {
  let higherPermissions;
  let roleName;

  rolesPermissions.forEach((rolePermissions) => {
    const actions = rolePermissions.actions || [
      PROGRESS_TRACKING_PERMISSION_ACTIONS.CONFIGURE,
    ];
    if (
      !higherPermissions ||
      (higherPermissions &&
        actionsToLevel(actions) > actionsToLevel(higherPermissions))
    ) {
      higherPermissions = actions;
      roleName = rolesInfo.find(
        (role) => role.memberGroupId === rolePermissions.uid
      ).name;
    }
  });

  return {
    rolePermissionLevel: actionsToLevel(higherPermissions),
    roleName,
  };
};

const getLevelLabel = (level, T) =>
  T.translate(
    `PERMISSIONS.LABELS.${Object.keys(PROGRESS_TRACKING_PERMISSION_LEVELS).find(
      (key) => PROGRESS_TRACKING_PERMISSION_LEVELS[key] === level
    )}`
  );

const getInheritedPermissionData = (
  memberPermissionLevel,
  rolePermissionsLevel,
  selectedRoleName,
  selectedAccessLevel,
  T
) => {
  if (selectedAccessLevel === SERVICE_ACCESSES.ADMIN) {
    return {
      level: PROGRESS_TRACKING_PERMISSION_LEVELS.MANAGE,
      levelName: T.translate('PERMISSIONS.LABELS.MANAGE'),
      subjectType: T.translate('PERMISSIONS.TYPES.ROLE_TEXT'),
      subjectName: 'Admin',
    };
  }

  let level = memberPermissionLevel;
  let subjectName;
  let subjectType;

  if (rolePermissionsLevel > level) {
    level = rolePermissionsLevel;
    subjectType = T.translate('PERMISSIONS.TYPES.ROLE_TEXT');
    subjectName = selectedRoleName;
  }

  return level !== memberPermissionLevel
    ? {
        level,
        levelName: getLevelLabel(level, T),
        subjectType,
        subjectName,
      }
    : null;
};

const getSelectOptions = (T) =>
  Object.entries(PROGRESS_TRACKING_PERMISSION_LEVELS).reduce(
    (result, [permissionsName, level]) => {
      if (level === PROGRESS_TRACKING_PERMISSION_LEVELS.MANAGE) return result;

      const pillValue = getPillValue(level);
      const option = {
        label: T.translate(`PERMISSIONS.LABELS.${permissionsName}`),
        pillValue,
        options: [
          {
            value: level,
            label: T.translate(
              `PERMISSIONS.PROGRESS_TRACKING.${permissionsName}_DESCRIPTION`
            ),
            pillValue,
          },
        ],
      };
      result.push(option);

      return result;
    },
    []
  );

export const getProgressTrackingPermissionsInfo = (
  memberPermissions,
  rolesPermissions,
  selectedAccessLevel,
  selectedRoles,
  T
) => {
  const memberPermissionLevel = actionsToLevel(
    memberPermissions?.actions || [
      PROGRESS_TRACKING_PERMISSION_ACTIONS.CONFIGURE,
    ]
  );
  const { rolePermissionLevel, roleName } =
    rolesPermissions.length > 0
      ? getRolePermissions(rolesPermissions, selectedRoles)
      : { rolePermissionLevel: 0 };
  const inheritedPermissionData = getInheritedPermissionData(
    memberPermissionLevel,
    rolePermissionLevel,
    roleName,
    selectedAccessLevel,
    T
  );
  const inheritedPermissions = !!inheritedPermissionData;
  let inheritanceInfo;
  let permissionsIndicatorProps;
  if (inheritedPermissions) {
    const { level, levelName, subjectType, subjectName } =
      inheritedPermissionData;
    inheritanceInfo = T.translate('INHERITED_PERMISSIONS_INFO', {
      subjectType,
      subjectName,
    });
    permissionsIndicatorProps = {
      label: levelName,
      pillValue: getPillValue(level),
    };
  }

  return {
    selectValue: memberPermissionLevel && { value: memberPermissionLevel },
    permissionsIndicatorProps,
    inheritedPermissions,
    inheritanceInfo,
    selectOptions: getSelectOptions(T, memberPermissionLevel?.ACTION),
    selectType: PermissionSelect.displayName,
    selectValueLabel: getLevelLabel(memberPermissionLevel, T),
    pillValue: getPillValue(memberPermissionLevel),
  };
};
